<template>
  <v-card>
    <v-card-title class="primary tertiary--text headline">
      <span>{{ $t('mapXProfile', { x: $t(propTitleKey) }) }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh hideFullscreen @close="askForConfirmation"/>
    </v-card-title>

    <v-card-text class="pt-4 pb-0">
      <v-alert
        v-model="showInfoAlert"
        text
        dismissible
        outlined
        color="info"
        class="mb-0"
      >
        <div class="d-flex align-center">
          <v-icon color="info" class="mr-3">mdi-information</v-icon>
          <span>{{ $t(propAlertKey, { x: $t(propLabelKey).toLowerCase() }) }}</span>
        </div>
      </v-alert>
      <DataTable
        :items="rows"
        :headers="headers"
        :customCells="customColumns"
        tableKey="externalName"
        :hideHeader="rows.length <= searchThreshold"
        disablePagination
      >
        <template #externalName="{ item }">
          <div class="d-flex align-center py-1">
            <v-icon
              small
              :color="isMapped(item) ? 'success' : 'grey lighten-1'"
              class="mr-2"
            >
              {{ isMapped(item) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
            </v-icon>
            <span class="font-weight-medium subtitle-1 mr-15 ml-4">{{ item.externalName }}</span>
            <v-icon class="ml-10">mdi-arrow-right</v-icon>
          </div>
        </template>

        <template #valueSelect="{ item }">
          <v-autocomplete
            v-model="item.value"
            :items="item.candidateOptions"
            item-value="value"
            item-text="text"
            :label="$t(propLabelKey)"
            :hint="rowHint(item)"
            persistent-hint
            dense
            outlined
            clearable
            hide-details="auto"
            color="primary"
            class="my-2"
          >
            <template #item="{ item: option }">
              <div class="d-flex align-center" style="width: 100%;">
                <span>{{ option.text }}</span>
                <v-spacer/>
                <Icon
                  v-if="isWarned(option)"
                  icon="mdi-alert"
                  iconColor="warning"
                  margin="ml-2"
                  :tooltipText="optionWarning(option)"
                  style="line-height: 0;"
                />
              </div>
            </template>
            <template #selection="{ item: option }">
              {{ option.text }}
              <Icon
                v-if="isWarned(option)"
                icon="mdi-alert"
                iconColor="warning"
                margin="ml-2"
                :tooltipText="optionWarning(option)"
                style="line-height: 0;"
              />
            </template>
            <template #message="{ message }">
              <span :class="{ 'warning--text': isWarned(selectedOption(item)) }">{{ message }}</span>
            </template>
          </v-autocomplete>
        </template>
      </DataTable>
    </v-card-text>

    <v-divider/>
    <v-card-actions class="px-6 py-3">
      <span class="caption text--secondary">
        {{ $t('importMappingsProgress', { mapped: mappedCount, total: rows.length }) }}
      </span>
      <v-spacer/>
      <v-btn color="primary" @click="confirmButtonClicked">{{ $t('confirm') }}</v-btn>
    </v-card-actions>

    <Dialog :stateId="dialogId" @close="resetDialogs">
      <ConfirmDialog
        v-if="confirmDialogOpen"
        :title="$t('closeDialogWithoutImporting')"
        :body="$t('closeImportDialogWithoutImporting')"
        :confirmText="$t('close')"
        width="20vw"
        @cancel="resetDialogs()"
        @confirm="confirmCloseDialog()"
      />
      <ConfirmDialog
        v-if="confirmWarnedOpen"
        :title="$t('confirmWarnedImportMappingsTitle')"
        :body="$t('confirmWarnedImportMappingsBody')"
        :confirmText="$t('saveMappings')"
        color="warning"
        width="50vw"
        @cancel="resetDialogs()"
        @confirm="confirmWarnedMappings()"
      >
        <template #custom-body>
          <DataTable
            :items="warnedTicketRows"
            :headers="warnedTicketHeaders"
            tableKey="key"
            :hideHeader="warnedTicketRows.length <= searchThreshold"
            :propItemsPerPage="10"
          />
        </template>
      </ConfirmDialog>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import { uniqueDialogId } from '@/utils/componentHelpers'
export default {
  name: 'MapImportProfileValuesDialog',
  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    propMappings: {
      type: Array,
      required: true
    },
    propTitleKey: {
      type: String,
      required: true
    },
    propLabelKey: {
      type: String,
      required: true
    },
    propAlertKey: {
      type: String,
      required: true
    },
    propWarningKey: {
      type: String,
      required: false,
      default: undefined
    }
  },

  data: () => ({
    rows: [],
    searchThreshold: 8,
    warningTicketLimit: 5,
    dialogId: uniqueDialogId('map-import-values-dialog'),
    confirmDialogOpen: false,
    confirmWarnedOpen: false,
    showInfoAlert: true
  }),

  created () {
    this.rows = this.propMappings.map(m => ({
      ...m,
      value: m.candidateOptions?.length === 1 ? m.candidateOptions[0].value : undefined
    }))
  },

  computed: {
    headers () {
      return [
        { text: this.$t('externalName'), value: 'externalName', align: 'start', sortable: false, width: '40%' },
        { text: this.$t(this.propLabelKey), value: 'value', align: 'start', sortable: false, width: '60%' }
      ]
    },
    customColumns () {
      return [
        { slotName: 'externalName', value: 'externalName' },
        { slotName: 'valueSelect', value: 'value' }
      ]
    },
    mappedCount () {
      return this.rows.filter(r => this.isMapped(r)).length
    },
    warnedRows () {
      return this.rows.filter(r => this.isWarned(this.selectedOption(r)))
    },
    warnedTicketHeaders () {
      return [
        { text: this.$t('ticketNumber'), value: 'ticketNumber', align: 'start' },
        { text: this.$t('externalName'), value: 'externalName', align: 'start' },
        { text: this.$t(this.propLabelKey), value: 'mappedTo', align: 'start' },
        { text: this.$t('reason'), value: 'reason', align: 'start', sortable: false }
      ]
    },
    warnedTicketRows () {
      return this.warnedRows.flatMap(row => {
        const option = this.selectedOption(row)
        return option.invalidTickets.map(({ ticketNumber, issueKey }) => ({
          key: `${row.externalName}-${ticketNumber}`,
          ticketNumber,
          externalName: row.externalName,
          mappedTo: option.text,
          reason: this.$t(issueKey)
        }))
      })
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    isMapped (row) {
      return row.value != null
    },
    selectedOption (row) {
      return row.candidateOptions?.find(o => o.value === row.value)
    },
    isWarned (option) {
      return option?.invalidTickets?.length > 0
    },
    optionWarning (option) {
      if (!this.isWarned(option)) return undefined
      return this.$t(this.propWarningKey, { tickets: this.ticketList(option.invalidTickets) })
    },
    ticketList (tickets) {
      const shown = tickets.slice(0, this.warningTicketLimit).map(t => `#${t.ticketNumber}`)
      const hiddenCount = tickets.length - shown.length
      if (hiddenCount > 0) shown.push(this.$t('nMore', { n: hiddenCount }))
      return shown.join(', ')
    },
    isOnlyOptionSelected (row) {
      return row.candidateOptions?.length === 1 && row.value === row.candidateOptions[0].value
    },
    rowHint (row) {
      const warning = this.optionWarning(this.selectedOption(row))
      if (warning) return warning
      if (this.isOnlyOptionSelected(row)) return this.$t('importMappingOnlyOptionHint', { x: this.$t(this.propLabelKey).toLowerCase() })
      return undefined
    },
    confirmButtonClicked () {
      if (this.warnedRows.length > 0) {
        this.confirmWarnedOpen = true
        this.openOrUpdateDialog({ id: this.dialogId, width: '50vw' })
        return
      }
      this.emitMappingsSelected()
    },
    emitMappingsSelected () {
      this.$emit('mappings-selected', this.rows.map(r => ({ externalName: r.externalName, value: r.value })))
    },
    confirmWarnedMappings () {
      this.emitMappingsSelected()
      this.resetDialogs()
    },
    askForConfirmation () {
      this.confirmDialogOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '20vw' })
    },
    resetDialogs () {
      this.confirmDialogOpen = false
      this.confirmWarnedOpen = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },
    confirmCloseDialog () {
      this.$emit('close')
      this.resetDialogs()
    }
  }
}
</script>
